import { Grid2, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import i18n from "../../../../../i18n/i18n";
import { zoullooColors } from "../../../../../theme";
import { AvailabilityViewModel } from "../../../../../ViewModels/FreelancerOnboardingMyAvailabilityStepViewModel";

interface MissionEndDatePickerProps {
	readonly value: Dayjs | null;
	readonly selectedAvailability: AvailabilityViewModel | null;
	readonly onChange: (value: Dayjs | null) => void;
	readonly onBlur: () => void;
	readonly touched: boolean;
	readonly error: string | undefined;
	readonly isValid: boolean;
}

export default function MissionEndDatePicker({
	value,
	selectedAvailability,
	onChange,
	onBlur,
	touched,
	error,
	isValid
}: MissionEndDatePickerProps) {
	const { t } = useTranslation();

	return (
		<Grid2
			container
			spacing={2}
			sx={{
				display: selectedAvailability === "onMission" ? "block" : "none"
			}}
		>
			<Grid2 size={{ xs: 12 }}>
				<Typography variant="body1" sx={{ color: zoullooColors.grey[700], mb: 0 }}>
					{t("freelancerOnboardingMyAvailability.missionUntil")}
				</Typography>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 8 }} sx={{ mt: 2 }}>
				<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
					<DatePicker
						value={value}
						onChange={onChange}
						views={["year", "month", "day"]}
						format="DD/MM/YYYY"
						minDate={dayjs()}
						slotProps={{
							textField: {
								fullWidth: true,
								variant: "outlined",
								sx: {
									"& .MuiInputBase-input": {
										paddingLeft: 2.5
									},
									"& .MuiOutlinedInput-root": {
										height: "50px",
										"& fieldset": {
											borderColor: zoullooColors.grey[400]
										},
										"&:hover fieldset": {
											borderColor: zoullooColors.grey[700]
										},
										"&.Mui-focused fieldset": {
											borderColor: "#fcce03"
										}
									}
								},
								onBlur: onBlur
							},
							yearButton: {
								sx: {
									"&.Mui-selected": {
										backgroundColor: `${zoullooColors.secondary.main} !important`,
										color: "#ffffff !important",
										"&:hover": {
											backgroundColor: `${
												zoullooColors.secondary.dark || zoullooColors.secondary.main
											} !important`
										}
									}
								}
							},
							monthButton: {
								sx: {
									"&.Mui-selected": {
										backgroundColor: `${zoullooColors.secondary.main} !important`,
										color: "#ffffff !important",
										"&:hover": {
											backgroundColor: `${
												zoullooColors.secondary.dark || zoullooColors.secondary.main
											} !important`
										}
									},
									"&.Mui-disabled": {
										backgroundColor: "#f5f5f5 !important",
										color: "#bdbdbd !important",
										cursor: "not-allowed !important",
										"&:hover": {
											backgroundColor: "#f5f5f5 !important"
										}
									}
								}
							},
							day: {
								sx: {
									"&.Mui-selected": {
										backgroundColor: `${zoullooColors.secondary.main} !important`,
										color: zoullooColors.secondary.contrastText,
										"&:hover": {
											backgroundColor: `${
												zoullooColors.secondary.dark || zoullooColors.secondary.main
											} !important`
										}
									},
									"&.MuiButtonBase-root": {
										"&.MuiPickersDay-today": {
											backgroundColor: `${zoullooColors.secondary.light} !important`,
											color: zoullooColors.secondary.contrastText
										},
										"&:hover": {
											backgroundColor: `${zoullooColors.secondary.light} !important`,
											color: zoullooColors.secondary.contrastText
										}
									},
									"&.Mui-disabled": {
										backgroundColor: "#f5f5f5 !important",
										color: "#bdbdbd !important",
										cursor: "not-allowed !important",
										"&:hover": {
											backgroundColor: "#f5f5f5 !important"
										}
									}
								}
							}
						}}
					/>
				</LocalizationProvider>
			</Grid2>
			{touched && error && (!value || !isValid) && (
				<Grid2 size={{ xs: 12 }}>
					<Typography color="error" variant="body1">
						{error}
					</Typography>
				</Grid2>
			)}
		</Grid2>
	);
}
